import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Stack,
  Paper,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  Star as StarIcon,
  Security as SecurityIcon,
  WorkspacePremium as PremiumIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DocumentService from '../services/DocumentService';
import SupportService from '../services/SupportService';
import { useUserType } from '../hooks/useUserType';

const REQUIRED_DOC_TYPES = ['white_card'];
const RECOMMENDED_DOC_TYPES = ['id_photo', 'ticket_license_certificate'];

const benefits = [
  { icon: PremiumIcon, text: 'Exclusive access to higher-rate construction opportunities' },
  { icon: SecurityIcon, text: 'Priority placement for certified workers with White Card + tickets' },
  { icon: GroupsIcon, text: 'Join private Skilled Network groups on Telegram & WhatsApp' },
  { icon: StarIcon, text: 'Profile badge showing verified skills to employers' },
];

const SkilledNetworkApply = () => {
  const navigate = useNavigate();
  const { userTypeData } = useUserType();
  const jobSeekerId = userTypeData?.profiles?.jobSeeker?.id;
  const profileName = userTypeData?.profiles?.jobSeeker
    ? `${userTypeData.profiles.jobSeeker.firstName || ''} ${userTypeData.profiles.jobSeeker.lastName || ''}`.trim()
    : '';

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const docService = new DocumentService();

  const fetchDocuments = useCallback(async () => {
    if (!jobSeekerId) return;
    try {
      setLoading(true);
      const docs = await docService.getMyDocuments();
      setDocuments(docs || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [jobSeekerId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const docTypes = new Set(documents.map((d) => d.documentType));
  const hasWhiteCard = docTypes.has('white_card');
  const hasIdPhoto = docTypes.has('id_photo');
  const hasTicket = docTypes.has('ticket_license_certificate');
  const canApply = hasWhiteCard;

  const handleApply = async () => {
    try {
      setSubmitting(true);
      await SupportService.createCase({
        title: 'Skilled Network Access Application',
        description:
          'I would like to apply for Skilled Network access. I have uploaded my required documents (White Card and relevant tickets/certificates) for verification.',
        linkedProfileId: jobSeekerId,
        linkedProfileType: 'jobseeker',
        linkedProfileName: profileName || jobSeekerId,
      });
      setSubmitted(true);
      setSnackbar({ open: true, message: 'Application submitted successfully!', severity: 'success' });
    } catch (err) {
      console.error('Failed to submit application:', err);
      setSnackbar({ open: true, message: 'Failed to submit application. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Container>
    );
  }

  return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={4}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            variant="text"
            color="inherit"
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to Dashboard
          </Button>

          {/* Hero */}
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              color: 'primary.contrastText',
              textAlign: 'center',
            }}
          >
            <PremiumIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Skilled Network Access
            </Typography>
            <Typography variant="body1">
              Exclusive access for certified workers with White Card + construction tickets. Higher-rate opportunities await.
            </Typography>
          </Paper>

          {/* Benefits */}
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Benefits
            </Typography>
            <List dense>
              {benefits.map((b, i) => (
                <ListItem key={i}>
                  <ListItemIcon>
                    <b.icon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={b.text} />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Document Checklist */}
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Document Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload the required documents before applying. A White Card is mandatory.
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {hasWhiteCard ? (
                  <CheckIcon color="success" />
                ) : (
                  <MissingIcon color="error" />
                )}
                <Typography variant="body2" fontWeight={hasWhiteCard ? 500 : 400}>
                  White Card
                </Typography>
                <Chip
                  label="Required"
                  color={hasWhiteCard ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                {hasIdPhoto ? (
                  <CheckIcon color="success" />
                ) : (
                  <MissingIcon color="warning" />
                )}
                <Typography variant="body2">Photo ID</Typography>
                <Chip label="Recommended" color="info" size="small" variant="outlined" />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                {hasTicket ? (
                  <CheckIcon color="success" />
                ) : (
                  <MissingIcon color="warning" />
                )}
                <Typography variant="body2">Tickets / Licences / Certificates</Typography>
                <Chip label="Recommended" color="info" size="small" variant="outlined" />
              </Stack>
            </Stack>

            {!hasWhiteCard && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Missing Required Documents</AlertTitle>
                You must upload at least a White Card before applying. Go to your profile to upload documents.
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => navigate('/jobseeker/profile-management')}
                >
                  Upload Documents
                </Button>
              </Alert>
            )}
          </Paper>

          {/* Submit */}
          {submitted ? (
            <Alert severity="success">
              <AlertTitle>Application Submitted</AlertTitle>
              Your Skilled Network access application has been submitted. Our team will review your documents and get back to you.
              You can track the status in your <Button size="small" onClick={() => navigate('/support')}>Support Cases</Button>.
            </Alert>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                disabled={!canApply || submitting}
                onClick={handleApply}
                sx={{ px: 6, py: 1.5 }}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PremiumIcon />}
              >
                {submitting ? 'Submitting…' : 'Apply for Skilled Network Access'}
              </Button>
              {!canApply && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Upload the required documents to enable the application button.
                </Typography>
              )}
            </Box>
          )}
        </Stack>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default SkilledNetworkApply;
